/* To interact with errors, dispatch or reset them, please use hooks */

import React, { ReactNode, useReducer } from 'react';
import { AppErrorName } from './AppErrorMap';

type Props = {
  children: ReactNode;
};

type ActionType = 'error' | 'reset';
type DispatchAction = { type: ActionType; error: AppErrorName };

export interface AppErrorContextProps {
  errors: AppErrorName[];
  dispatch: React.Dispatch<DispatchAction>;
}

export const AppErrorContext = React.createContext<AppErrorContextProps>({
  errors: [],
  dispatch: () => undefined,
});

/*
  Choosing reducer approach, because React guarantees that dispatch function identity is stable and won’t change on re-renders.
  Which means users of AppErrorContext will only re-render on state change not on Dispatch change.
  This prevents getting into infinite loops, when we need to add Dispatch or functions using it to dependencies list.
  For example in useAppErrorDispatcher.

  (Actually, it would be safe to omit dispatch from the useEffect or useCallback dependency list, but our linter won't allow it).
*/
function reducer(
  errors: AppErrorName[],
  action: DispatchAction
): AppErrorName[] {
  switch (action.type) {
    case 'error': {
      const cleanedUp = removeDuplicates(errors, action.error);
      return [...cleanedUp, action.error];
    }
    case 'reset': {
      const modified = removeDuplicates(errors, action.error);
      return modified;
    }
    default:
      return errors;
  }
}

export const AppErrorBoundary = ({ children }: Props): JSX.Element => {
  const [errorNames, dispatch] = useReducer(reducer, []);

  const context = {
    errors: errorNames,
    dispatch,
  };

  return (
    <AppErrorContext.Provider value={context}>
      {children}
    </AppErrorContext.Provider>
  );
};

const removeDuplicates = (arr: AppErrorName[], errName: AppErrorName) => {
  return arr.filter((i) => i !== errName);
};
